import { inferTemplateFileCategories } from "@/lib/utils/inferTemplateCategories";
import { FileViewer } from "@/components/features/matter/file-repo/FileViewer";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import {
  ActionConfirmationModal,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSubmitButton,
  MultiselectFormField,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMatterTemplate,
  useDeleteMatterTemplate,
  useUpdateMatterTemplate,
} from "@/lib/data/react-query/matters/matter-template";
import { MatterTemplateSchema } from "@/lib/data/schemas/matters/MatterSchema";
import { acceptedFileMimeTypesDocumentOnly } from "@/lib/data/static/fileStatics";
import { MATTER_TEMPLATE_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";

import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MatterTemplate } from "@/lib/types";
import { getFileNameParts } from "@/lib/utils/matterFile";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileText, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { CreateDocxSlideover } from "@/components/features/matter/file-repo/CreateDocxSlideover";
import { EditDocxTemplateModal } from "./EditDocxTemplateModal";

interface AddOrEditTemplateModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

type ConditionalProps =
  | {
      isReadOnly?: boolean;
      editing: true;
      activeMatterTemplate?: MatterTemplate;
      setActiveMatterTemplateId: (id: string) => void;
      autoOpenUpload?: never;
    }
  | {
      isReadOnly?: never;
      editing: false;
      activeMatterTemplate?: never;
      setActiveMatterTemplateId?: never;
      autoOpenUpload?: boolean;
    };

const formSchema = MatterTemplateSchema.pick({
  name: true,
  description: true,
  category: true,
}).extend({
  matterTemplateFile: z.string().min(1, {
    message: "File is required",
  }),
});

const defaultFormValues = {
  name: "",
  description: "",
  category: [],
  matterTemplateFile: "",
};

export function AddOrEditTemplateModal({
  open,
  setOpen,
  activeMatterTemplate,
  editing,
  isReadOnly,
  autoOpenUpload,
}: AddOrEditTemplateModalProps & ConditionalProps) {
  const { hasActionPermission } = useUser();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCreateDocxOpen, setIsCreateDocxOpen] = useState(false);
  const autoOpenUploadHandledRef = useRef(false);
  const [file, setFile] = useState<
    | {
        file: File;
        url: string;
      }
    | undefined
  >();
  const [isDocxEditorOpen, setIsDocxEditorOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: editing
      ? {
          ...defaultFormValues,
          ...activeMatterTemplate,
          matterTemplateFile: "editing",
          name: getFileNameParts(activeMatterTemplate?.name).fileName,
        }
      : defaultFormValues,
    mode: "onSubmit",
  });

  const manualCategoriesRef = useRef(false);
  const [isInferringCategories, setIsInferringCategories] = useState(false);
  const [categoryInferenceFailed, setCategoryInferenceFailed] = useState(false);
  const [hasAutofilledCategories, setHasAutofilledCategories] = useState(false);

  useEffect(() => {
    const subscription = form.watch((_values, { name, type }) => {
      if (name === "category" && type === "change") {
        manualCategoriesRef.current = true;
        setHasAutofilledCategories(false);
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [form]);

  useEffect(() => {
    setHasAutofilledCategories(false);
    if (!open) manualCategoriesRef.current = false;
    if (!open || editing) return;
    let cancelled = false;
    setCategoryInferenceFailed(false);
    setIsInferringCategories(Boolean(file));
    if (!file) {
      if (!manualCategoriesRef.current) form.setValue("category", []);
      return;
    }
    void inferTemplateFileCategories(file.file)
      .then((categories) => {
        if (!cancelled && !manualCategoriesRef.current) {
          form.setValue("category", categories);
          setHasAutofilledCategories(categories.length > 0);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCategoryInferenceFailed(true);
          if (!manualCategoriesRef.current) form.setValue("category", []);
        }
      })
      .finally(() => {
        if (!cancelled) setIsInferringCategories(false);
      });
    return () => {
      cancelled = true;
    };
  }, [file, open, editing, form]);

  const createMatterTemplate = useCreateMatterTemplate();
  const editMatterTemplate = useUpdateMatterTemplate();
  const deleteMatterTemplate = useDeleteMatterTemplate();
  const isFormLoading =
    isInferringCategories ||
    createMatterTemplate.isPending ||
    editMatterTemplate.isPending ||
    deleteMatterTemplate.isPending;
  const activeTemplateFileExtension =
    editing && activeMatterTemplate?.presignedUrlKey
      ? getFileNameParts(activeMatterTemplate.presignedUrlKey).fileExtension
      : "";
  const templateFileNameParts = getFileNameParts(activeMatterTemplate?.name);
  const activeTemplateFileName = templateFileNameParts.fileExtension
    ? activeMatterTemplate?.name
    : activeMatterTemplate?.presignedUrlKey;
  const uploadedFileExtension = file
    ? getFileNameParts(file.file.name).fileExtension
    : "";
  const isActiveDocx =
    Boolean(activeTemplateFileName) &&
    getFileNameParts(activeTemplateFileName).fileExtension === "docx";
  const canEditDocx = Boolean(
    !isReadOnly &&
      isActiveDocx &&
      hasActionPermission(
        ACTION_PERMISSION_KEYS.matterTemplate,
        ACTION_PERMISSION_VALUES.update,
      ),
  );

  const handleRemoveFileUpload = () => {
    setFile(undefined);
  };

  const handleFileUpload = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const { fileName } = getFileNameParts(file.name);
    if (!form.getValues("name")) {
      form.setValue("name", fileName, { shouldDirty: true });
    }
    const objectUrl = URL.createObjectURL(file);
    setFile({ file: file, url: objectUrl });
  };

  const handleDocxFileCreate = (createdFile: File) => {
    const { fileName } = getFileNameParts(createdFile.name);
    if (!form.getValues("name")) {
      form.setValue("name", fileName, { shouldDirty: true });
    }
    form.setValue("matterTemplateFile", createdFile.name, {
      shouldDirty: true,
      shouldValidate: true,
    });
    const objectUrl = URL.createObjectURL(createdFile);
    setFile({ file: createdFile, url: objectUrl });
  };

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const { category, matterTemplateFile, ...rest } = data;
    const categories = category ?? [];
    let matterTemplateFileName = data.name;
    const fileExtension =
      getFileNameParts(matterTemplateFile).fileExtension ||
      getFileNameParts(activeMatterTemplate?.presignedUrlKey).fileExtension;
    matterTemplateFileName += `.${fileExtension}`;

    if (editing) {
      await editMatterTemplate.mutateAsync({
        id: activeMatterTemplate?.id || "",
        dateModified: activeMatterTemplate?.dateModified || "",
        ...rest,
        category: categories,
        name: matterTemplateFileName,
        type: fileExtension || undefined,
      });
    } else {
      if (!file) return;
      await createMatterTemplate.mutateAsync({
        ...rest,
        category: categories,
        file: file.file,
        name: matterTemplateFileName,
        type: fileExtension || undefined,
      });
    }
    setOpen(false);
    form.reset();
  };

  const handleDelete = async () => {
    setOpen(false);
    if (editing) {
      await deleteMatterTemplate
        .mutateAsync({
          id: activeMatterTemplate?.id || "",
        })
        .then(() => {
          setIsDeleting(false);
        });
    }
  };

  const sheetTitle = useMemo(
    () =>
      isReadOnly
        ? "View template"
        : editing
          ? "Update template"
          : autoOpenUpload
            ? "Upload template"
            : "Create template",
    [isReadOnly, editing, autoOpenUpload],
  );

  const sheetDescription = useMemo(
    () =>
      isReadOnly
        ? "View the assignee template details below"
        : editing
          ? "Update the assignee template details below"
          : autoOpenUpload
            ? "Upload a matter template for your firm"
            : "Create a new matter template for your firm",
    [isReadOnly, editing, autoOpenUpload],
  );

  useEffect(() => {
    if (!open) {
      setFile(undefined);
      setIsCreateDocxOpen(false);
      form.reset();
      setIsDocxEditorOpen(false);
    }
  }, [open, form]);

  useEffect(() => {
    if (!open || editing) {
      autoOpenUploadHandledRef.current = false;
      return;
    }
    if (!autoOpenUpload) {
      autoOpenUploadHandledRef.current = false;
      return;
    }
    if (file) return;
    if (autoOpenUploadHandledRef.current) return;

    autoOpenUploadHandledRef.current = true;
    const timeoutId = window.setTimeout(() => {
      fileInputRef.current?.click();
    }, 100);

    return () => {
      window.clearTimeout(timeoutId);
      autoOpenUploadHandledRef.current = false;
    };
  }, [open, editing, autoOpenUpload, file]);

  const TEMPLATE_FORM_ID = "add-or-edit-template-form";
  const submitFormId = TEMPLATE_FORM_ID;
  const canDeleteTemplate =
    editing &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterTemplate,
      ACTION_PERMISSION_VALUES.delete,
    );

  const deleteButton = canDeleteTemplate ? (
    <div>
      <Button
        type="button"
        variant="destructive"
        onClick={() => {
          setIsDeleting(true);
        }}
      >
        Delete
      </Button>
    </div>
  ) : null;

  const footerActions = (
    <>
      {deleteButton}
      <div className="flex grow justify-end space-x-2">
        <FormCancelButton
          label={isReadOnly ? "Close" : "Cancel"}
          setOpen={setOpen}
          isDirty={form.formState.isDirty}
        />
        {!isReadOnly && <FormSubmitButton submitFormId={submitFormId} />}
      </div>
    </>
  );

  const previewNode = file ? (
    <FileViewer
      fileType="matter-template"
      file={{
        file: file.file,
        url: file.url,
      }}
      showDownload={false}
      handleRemoveFileUpload={handleRemoveFileUpload}
      wrapperClassName="h-full"
      viewerClassName="min-h-[55vh] flex-1"
    />
  ) : activeMatterTemplate?.presignedUrlKey ? (
    <FileViewer
      fileType="matter-template"
      file={{
        fileName: activeTemplateFileName ?? "",
        presignedURLKey: activeMatterTemplate.presignedUrlKey || "",
      }}
      showDocxModeToggle={isActiveDocx}
      canEditDocx={canEditDocx}
      onDocxEdit={
        canEditDocx
          ? () => {
              setIsDocxEditorOpen(true);
            }
          : undefined
      }
      wrapperClassName="h-full"
      viewerClassName="min-h-[55vh] flex-1"
    />
  ) : null;

  const templateFormFields = (
    <>
      {isFormLoading && <FormLoadingOverlay />}
      <fieldset disabled={isReadOnly} className={slideoverFieldsetClassName}>
        <TextFormField register={form.register("name")} label="Name*" />
        {(uploadedFileExtension || activeTemplateFileExtension) && (
          <Typography variant="subHeading" className="mb-3 self-end">
            .{uploadedFileExtension || activeTemplateFileExtension}
          </Typography>
        )}
        <div className="col-span-2">
          <TextAreaFormField
            register={form.register("description")}
            label="Description"
          />
        </div>
        <div className="col-span-2">
          <MultiselectFormField
            label="Categories"
            options={MATTER_TEMPLATE_CATEGORY_OPTIONS.map((option) => ({
              label: option,
              value: option,
            }))}
            placeholder="Select categories"
            maxCount={5}
            register={form.register("category")}
          />
          {!editing && (categoryInferenceFailed || hasAutofilledCategories) && (
            <p className="text-muted-foreground mt-2 text-xs">
              {categoryInferenceFailed
                ? "Could not detect categories from this file. You can select them manually."
                : "Categories were added from this file’s document variables. You can change them before saving."}
            </p>
          )}
        </div>
        {!previewNode && (
          <FormField
            {...form.register("matterTemplateFile")}
            control={form.control}
            render={() => (
              <FormItem>
                <div className="flex">
                  <FormLabel>File</FormLabel>
                  <FormMessage
                    data-testid="error-message"
                    className="grow text-right"
                  />
                </div>
                <FormControl>
                  <>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Upload file
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setIsCreateDocxOpen(true);
                        }}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        Create template
                      </Button>
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={(e) => {
                        handleFileUpload(e.target.files);
                        form.setValue(
                          "matterTemplateFile",
                          e.target.files?.[0]?.name || "",
                        );
                      }}
                      accept={acceptedFileMimeTypesDocumentOnly.join(",")}
                    />
                  </>
                </FormControl>
              </FormItem>
            )}
          />
        )}
      </fieldset>
    </>
  );

  const supportingDialogs = (
    <>
      <ActionConfirmationModal
        title={`Are you sure you want to delete this matter template?`}
        open={isDeleting && !!activeMatterTemplate}
        setOpen={setIsDeleting}
        afterActionFn={async () => {
          if (activeMatterTemplate) {
            await handleDelete();
          }
        }}
      />

      <CreateDocxSlideover
        open={isCreateDocxOpen}
        setOpen={setIsCreateDocxOpen}
        title="Create template"
        description="Create a new template with fields."
        fileType="matter-template"
        initialFileName={form.getValues("name") || "New document"}
        onSave={(file) => {
          handleDocxFileCreate(file);
        }}
      />
      {activeMatterTemplate && isActiveDocx && (
        <EditDocxTemplateModal
          open={isDocxEditorOpen}
          setOpen={setIsDocxEditorOpen}
          template={activeMatterTemplate}
          onSaved={() => {
            setOpen(false);
          }}
        />
      )}
    </>
  );

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={sheetTitle}
          description={sheetDescription}
          isDirty={form.formState.isDirty}
          loading={isFormLoading}
          size={previewNode ? "3xl" : "2xl"}
          className={
            previewNode
              ? "h-[min(88vh,920px)] w-[calc(100vw-2rem)] max-w-6xl"
              : "w-[calc(100vw-2rem)] max-w-4xl"
          }
          bodyClassName={previewNode ? "min-h-0" : undefined}
          bodyContained={Boolean(previewNode)}
          preview={previewNode}
          previewLabel={null}
          previewColumnClassName={
            previewNode
              ? "lg:w-[min(100%,46%)] lg:max-w-[46%] bg-muted/20"
              : undefined
          }
          bodyFormId={TEMPLATE_FORM_ID}
          bodyFormOnSubmit={form.handleSubmit(onSubmit)}
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              {footerActions}
            </div>
          }
        >
          <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto">
            {templateFormFields}
          </div>
        </AppModal>
      </Form>
      {supportingDialogs}
    </>
  );
}
